import { z } from 'zod';

import { CashVarianceCaseStatusSchema } from './cash-variance.schema.js';
import {
  cashReportMonthSchema,
  cashReportTimestampSchema,
  CashReportActorRefDtoSchema,
  CashReportCashLocationRefDtoSchema,
  CashReportSessionRefDtoSchema,
  validateCashReportDateRange,
  validateCashReportMonthRange,
} from './cash-report-common.schema.js';
import {
  cashVarianceAgingBucketValues,
  type CashUnresolvedVarianceAgingAuditRiskBucketDto,
  type CashUnresolvedVarianceAgingAuditRiskRowDto,
  type CashUnresolvedVarianceAgingBucketDto,
  type CashUnresolvedVarianceAgingMonetaryRowDto,
  type CashVarianceAgingBucket,
  type CashVarianceMonthlySummaryReportItemDto,
  type GetCashUnresolvedVarianceAgingReportRequest,
  type GetCashUnresolvedVarianceAgingReportResponse,
  type GetCashVarianceMonthlySummaryReportRequest,
  type GetCashVarianceMonthlySummaryReportResponse,
} from './cash-report-variance.dto.js';

export const CashVarianceAgingBucketSchema: z.ZodType<CashVarianceAgingBucket> = z.enum(
  cashVarianceAgingBucketValues,
);

export const GetCashVarianceMonthlySummaryReportRequestSchema: z.ZodType<GetCashVarianceMonthlySummaryReportRequest> =
  z
    .object({
      companyId: z.uuid(),
      fromMonth: cashReportMonthSchema,
      toMonth: cashReportMonthSchema,
      storeId: z.uuid().optional(),
      locationId: z.uuid().optional(),
      stationId: z.uuid().optional(),
      cashLocationId: z.uuid().optional(),
      actorUserId: z.uuid().optional(),
    })
    .superRefine(validateCashReportMonthRange);

export const CashVarianceMonthlySummaryReportItemDtoSchema: z.ZodType<CashVarianceMonthlySummaryReportItemDto> =
  z.object({
    month: cashReportMonthSchema,
    shortageCreatedAmountMinor: z.number().int().nonnegative(),
    overageCreatedAmountMinor: z.number().int().nonnegative(),
    recoveredAmountMinor: z.number().int().nonnegative(),
    writtenOffAmountMinor: z.number().int().nonnegative(),
    offsetAmountMinor: z.number().int().nonnegative(),
    classifiedAmountMinor: z.number().int().nonnegative(),
    removedAmountMinor: z.number().int().nonnegative(),
    unresolvedAmountMinor: z.number().int().nonnegative(),
    shortageCaseCount: z.number().int().nonnegative(),
    overageCaseCount: z.number().int().nonnegative(),
    recoveryActionCount: z.number().int().nonnegative(),
    writeoffActionCount: z.number().int().nonnegative(),
    offsetActionCount: z.number().int().nonnegative(),
    classificationActionCount: z.number().int().nonnegative(),
    removalActionCount: z.number().int().nonnegative(),
    unresolvedCaseCount: z.number().int().nonnegative(),
    forceUnverifiedAuditRiskCount: z.number().int().nonnegative(),
  });

export const GetCashVarianceMonthlySummaryReportResponseSchema: z.ZodType<GetCashVarianceMonthlySummaryReportResponse> =
  z.object({ items: z.array(CashVarianceMonthlySummaryReportItemDtoSchema) });

export const GetCashUnresolvedVarianceAgingReportRequestSchema: z.ZodType<GetCashUnresolvedVarianceAgingReportRequest> =
  z
    .object({
      companyId: z.uuid(),
      storeId: z.uuid().optional(),
      locationId: z.uuid().optional(),
      stationId: z.uuid().optional(),
      cashLocationId: z.uuid().optional(),
      status: CashVarianceCaseStatusSchema.optional(),
      actorUserId: z.uuid().optional(),
      from: cashReportTimestampSchema.optional(),
      to: cashReportTimestampSchema.optional(),
      asOf: cashReportTimestampSchema.optional(),
    })
    .superRefine(validateCashReportDateRange);

export const CashUnresolvedVarianceAgingBucketDtoSchema: z.ZodType<CashUnresolvedVarianceAgingBucketDto> =
  z.object({
    bucket: CashVarianceAgingBucketSchema,
    unresolvedAmountMinor: z.number().int().nonnegative(),
    caseCount: z.number().int().nonnegative(),
  });

export const CashUnresolvedVarianceAgingAuditRiskBucketDtoSchema: z.ZodType<CashUnresolvedVarianceAgingAuditRiskBucketDto> =
  z.object({
    bucket: CashVarianceAgingBucketSchema,
    auditRiskCount: z.number().int().nonnegative(),
  });

export const CashUnresolvedVarianceAgingMonetaryRowDtoSchema: z.ZodType<CashUnresolvedVarianceAgingMonetaryRowDto> =
  z.object({
    bucket: CashVarianceAgingBucketSchema,
    caseId: z.uuid(),
    status: CashVarianceCaseStatusSchema,
    openedAt: cashReportTimestampSchema,
    ageDays: z.number().int().nonnegative(),
    initialAmountMinor: z.number().int(),
    unresolvedAmountMinor: z.number().int().nonnegative(),
    cashLocation: CashReportCashLocationRefDtoSchema,
    session: CashReportSessionRefDtoSchema.nullable(),
    openedBy: CashReportActorRefDtoSchema.nullable(),
  });

export const CashUnresolvedVarianceAgingAuditRiskRowDtoSchema: z.ZodType<CashUnresolvedVarianceAgingAuditRiskRowDto> =
  z.object({
    bucket: CashVarianceAgingBucketSchema,
    cashSessionId: z.uuid(),
    closedAt: cashReportTimestampSchema,
    ageDays: z.number().int().nonnegative(),
    expectedCashMinor: z.number().int(),
    countedCashMinor: z.null(),
    varianceAmountMinor: z.null(),
    auditRisk: z.literal(true),
    cashLocation: CashReportCashLocationRefDtoSchema,
    session: CashReportSessionRefDtoSchema,
    closedBy: CashReportActorRefDtoSchema.nullable(),
  });

export const GetCashUnresolvedVarianceAgingReportResponseSchema: z.ZodType<GetCashUnresolvedVarianceAgingReportResponse> =
  z.object({
    asOf: cashReportTimestampSchema,
    monetaryBuckets: z.array(CashUnresolvedVarianceAgingBucketDtoSchema),
    monetaryRows: z.array(CashUnresolvedVarianceAgingMonetaryRowDtoSchema),
    auditRiskBuckets: z.array(CashUnresolvedVarianceAgingAuditRiskBucketDtoSchema),
    auditRiskRows: z.array(CashUnresolvedVarianceAgingAuditRiskRowDtoSchema),
    unresolvedAmountMinor: z.number().int().nonnegative(),
    unresolvedCaseCount: z.number().int().nonnegative(),
    forceUnverifiedAuditRiskCount: z.number().int().nonnegative(),
  });
